import { proxyHost } from "../config";
import { getNewsLocale } from "../store/language";
import { getQueryMap } from "./queryMap";

const doShuffle = (array) => {
  array = [...array];

  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }

  return array;
};

const shuffleGroupedOrderedTiles = (originalItems) => {
  const items = [...originalItems].sort((a, b) => a.order - b.order);
  let lastOrder = null;
  let currentGroup = [];
  let result = [];

  for (const item of items) {
    if (lastOrder !== null && item.order !== lastOrder) {
      result = [...result, ...doShuffle(currentGroup)];
      currentGroup = [];
    }

    currentGroup.push(item);
    lastOrder = item.order;
  }

  if (currentGroup.length) {
    result = [...result, ...doShuffle(currentGroup)];
  }

  return result;
};

const orderTiles = (sectionData, component) => {
  const orderedItems = shuffleGroupedOrderedTiles(
    sectionData.filter((item) => item.order !== null),
  );
  const unorderedItems = ["sections.deals", "sections.top-games-section"].includes(
    component,
  )
    ? sectionData.filter((item) => item.order === null)
    : sectionData
        .filter((item) => item.order === null)
        .sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));
  return [...orderedItems, ...unorderedItems];
};

const shuffleTiles = (shuffle, data, component) =>
  shuffle ? doShuffle(data) : orderTiles(data, component);

const shufflefunc = (shuffle, data) => (shuffle ? doShuffle(data) : data);

export const getQuery = async (
  component: string,
  id: number,
  country: string | undefined,
  language: string,
  shuffle: boolean,
) => {
  const locale = getNewsLocale(language, country);

  const queryMap = getQueryMap(proxyHost, id, country, language, locale);

  const fetchDataFromUrl = async (targetUrl: string) => {
    try {
      const response = await fetch(targetUrl, { cache: "no-cache" });

      if (response.status === 200) {
        return response.json();
      }

      throw new Error(`Failed to fetch from ${targetUrl}`);
    } catch (error) {
      /* eslint-disable-next-line no-console */
      console.error(`Error fetching URL: ${(error as Error).message}`);
      throw error;
    }
  };

  const fetchUrl = async (url: string, fallbackUrl?: string) => {
    try {
      return await fetchDataFromUrl(url);
    } catch (urlError) {
      if (fallbackUrl) {
        try {
          return await fetchDataFromUrl(fallbackUrl);
        } catch (fallbackError) {
          /* eslint-disable-next-line no-console */
          console.error(
            `Error fetching fallback URL: ${(fallbackError as Error).message}`,
          );

          return null;
        }
      }

      return null;
    }
  };

  if (!component || !queryMap.has(component)) {
    return null;
  }

  if (component === "") {
    return null;
  }

  const urls = queryMap.get(component);
  let sections;

  if (Array.isArray(urls)) {
    const responses = await Promise.all(urls.map((url) => fetchUrl(url)));
    sections = responses.filter((response) => response !== null);
  } else {
    const url = urls;

    if (component === "sections.news-section") {
      sections = await fetchUrl(
        url!,
        `https://speeddials.opera.com/api/v3/news?_limit=200&category=ga&country=${country}&language=${language}&locale=en_zz`,
      );
    } else {
      sections = await fetchUrl(url!);
    }
  }

  if (!sections) {
    return [];
  }

  if (component === "sections.news-section") {
    return shufflefunc(shuffle, sections.news);
  }

  if (component === "sections.banner-section") {
    return sections.data.sectionType[0];
  }

  if (
    [
      "sections.deals",
      "sections.top-games-section",
      "sections.html-games-section",
      "sections.free-games",
    ].includes(component)
  ) {
    return shuffleTiles(shuffle, sections.data.sectionType[0].games, component);
  } else if (component === "sections.calendar-section") {
    return sections.data.sectionType[0].games;
  } else if (component === "sections.daily-section") {
    return sections.data.sectionType[0].items.reverse();
  } else if (component! === "sections.trailer-section") {
    return shufflefunc(shuffle, sections.data.sectionType[0].trailers.slice(0, 10));
  } else if (component! === "sections.video-section") {
    return [
      {
        id: sections.data.sectionType[0].id,
        title: "",
        thumbnail: sections.data.sectionType[0].thumbnail,
        trailer: sections.data.sectionType[0].video,
      },
    ];
  } else if (component === "sections.deal-aggregator") {
    return [
      ...shuffleTiles(shuffle, sections[0].data.sectionType[0].games, component),
      ...sections[1],
    ];
  }
};
